import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Play, Square, ChevronDown, Star, Settings, Wand2, Loader2, History } from 'lucide-react';
import { useProjectStore } from '@/stores/projectStore';
import { useTerminalStore } from '@/stores/terminalStore';
import { useBackend } from '@/services/backend';
import { useUIStore } from '@/stores/uiStore';
import { logger } from '@/lib/logger';
import {
  getProjectRunnerSettings,
  setProjectRunnerSettings,
  addToRunnerHistory,
  type RunConfiguration,
  type AteliercodeRunners,
} from '@/lib/projectSettings';
import { detectRunConfigurations, type DetectionContext } from '@/lib/runConfigDetector';

interface RunButtonProps {
  projectPath?: string;
  compact?: boolean;
}

export default function RunButton({ projectPath, compact }: RunButtonProps) {
  const backend = useBackend();
  const project = useProjectStore((s) => s.currentProject);
  const updateProjectInStore = useProjectStore((s) => s.updateProject);
  const openSettings = useUIStore((s) => s.openSettings);

  const safePath = projectPath ?? '__default__';

  // File-based configs from .ateliercode/runners.json (primary source)
  const [fileConfigs, setFileConfigs] = useState<RunConfiguration[] | null>(null);
  const [fileDefault, setFileDefault] = useState<string | null>(null);
  const [, setFileLoaded] = useState(false);

  // Fallback: legacy settings-blob configs
  const runnerSettings = getProjectRunnerSettings(project?.settings ?? null);
  const legacyConfigs = runnerSettings.configs;
  const legacyDefaultId = runnerSettings.defaultConfigId;

  // Use file-based configs if available, otherwise legacy
  const configs = fileConfigs ?? legacyConfigs;
  const defaultConfigId = fileDefault
    ? configs.find((c) => c.name === fileDefault)?.id ?? configs[0]?.id ?? null
    : legacyDefaultId;
  const { history } = runnerSettings; // history stays in settings blob

  // Run state: is there an active run terminal for this project?
  const runSessionId = useTerminalStore((s) => s.runSessionIds.get(safePath));
  const runSession = useTerminalStore((s) =>
    runSessionId != null ? s.sessions.get(runSessionId) : undefined,
  );
  const isRunning = runSession != null && runSession.status === 'active';

  // Dropdown state
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [detectedScripts, setDetectedScripts] = useState<RunConfiguration[] | null>(null);
  const [isAutoConfiguring, setIsAutoConfiguring] = useState(false);
  const chevronRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Load configs from .ateliercode/runners.json on mount + on window focus.
  // The window-focus reread covers the "Claude (or any external tool)
  // edits runners.json while the app is in the background" case the user
  // hit — without it, the only way to pick up the new configs was to
  // switch projects and back. The migration manager
  // (configMigration.ts) handles creating the file from the legacy blob
  // or defaults — we just read what's there.
  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    const projectId = project.id;

    const reread = async () => {
      try {
        const raw = await backend.files.readAteliercodeRunners(projectId);
        if (cancelled) return;

        if (raw) {
          const parsed: AteliercodeRunners = JSON.parse(raw);
          if (parsed.configs && Array.isArray(parsed.configs)) {
            let counter = 0;
            setFileConfigs(parsed.configs.map((c) => ({
              id: Date.now().toString(36) + (++counter).toString(36),
              name: c.name,
              command: c.command,
              shell: c.shell,
            })));
            setFileDefault(parsed.default ?? null);
          }
        }
        // If file doesn't exist, legacy configs are used as fallback
      } catch (e) {
        logger.warn('RunButton', 'Failed to load .ateliercode/runners.json', e);
      } finally {
        if (!cancelled) setFileLoaded(true);
      }
    };

    void reread();

    // Re-read whenever the user comes back to the AtelierCode window from
    // another app (Claude in a terminal, an external editor, etc.). Cheap:
    // single file read with a couple-hundred-byte payload.
    const onFocus = () => { void reread(); };
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [project?.id, backend]);

  // Reset ephemeral detection when project changes
  useEffect(() => {
    setDetectedScripts(null);
    setMenuOpen(false);
    setFileConfigs(null);
    setFileDefault(null);
    setFileLoaded(false);
  }, [project?.id]);

  // Click-outside handler
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        chevronRef.current && !chevronRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Auto-detect scripts when dropdown opens and no configs exist
  useEffect(() => {
    if (!menuOpen || configs.length > 0 || !project?.id || !project?.root_path) return;
    if (detectedScripts !== null) return; // already detected for this project

    const readFile = (path: string) => backend.files.readFile(project.id, path);
    backend.files.getFolderChildren(project.id, project.root_path)
      .then((nodes) => {
        const ctx: DetectionContext = { readFile, rootFiles: nodes.map((n) => n.name) };
        return detectRunConfigurations(ctx);
      })
      .then((results) => setDetectedScripts(results.length > 0 ? results : []))
      .catch(() => setDetectedScripts([]));
  }, [menuOpen, configs.length, project?.id, project?.root_path, backend, detectedScripts]);

  const toggleMenu = useCallback(() => {
    setMenuOpen((prev) => {
      if (prev) return false;
      if (chevronRef.current) {
        const rect = chevronRef.current.getBoundingClientRect();
        setMenuPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 220) });
      }
      return true;
    });
  }, []);

  const runConfig = useCallback(async (config: RunConfiguration) => {
    const store = useTerminalStore.getState();
    store.setPendingCommand({
      command: config.command,
      title: config.name,
      projectPath: safePath,
      shell: config.shell && config.shell !== 'default' ? config.shell : undefined,
      kind: 'run',
      runConfig: config.name,
    });
    // Signal workspace to switch to terminal tab so TerminalTab mounts
    // and picks up the pending command immediately
    store.requestTabSwitch();
    setMenuOpen(false);

    // Record in run history
    if (project?.id) {
      try {
        const updatedSettings = addToRunnerHistory(project.settings, {
          name: config.name,
          command: config.command,
        });
        await backend.projects.update(project.id, { settings: updatedSettings });
        await updateProjectInStore(project.id, { settings: updatedSettings });
      } catch {
        // Non-critical
      }
    }
  }, [safePath, project, backend, updateProjectInStore]);

  const stopRun = useCallback(() => {
    if (runSessionId != null) {
      backend.terminal.close(runSessionId).catch((e) => logger.warn('RunButton', 'close failed', e));
    }
  }, [runSessionId, backend]);

  const handleMainClick = useCallback(() => {
    if (isRunning) {
      stopRun();
      return;
    }
    // Find the default config or first config
    const defaultConfig = configs.find((c) => c.id === defaultConfigId) ?? configs[0];
    if (defaultConfig) {
      runConfig(defaultConfig);
    } else {
      // No configs — open the dropdown to show auto-detected or add option
      toggleMenu();
    }
  }, [isRunning, stopRun, configs, defaultConfigId, runConfig, toggleMenu]);

  /** Run detection, merge with existing configs, and persist to .ateliercode/runners.json. */
  const handleAutoConfigure = useCallback(async () => {
    if (!project?.id) return;
    setIsAutoConfiguring(true);
    try {
      const readFile = (path: string) => backend.files.readFile(project.id, path);
      const rootNodes = await backend.files.getFolderChildren(project.id, project.root_path);
      const ctx: DetectionContext = { readFile, rootFiles: rootNodes.map((n) => n.name) };
      const detected = await detectRunConfigurations(ctx);

      if (detected.length === 0) {
        setIsAutoConfiguring(false);
        return;
      }

      // Merge: skip detected configs whose command already exists
      const existingCommands = new Set(configs.map((c) => c.command));
      const newConfigs = detected.filter((c) => !existingCommands.has(c.command));
      const merged = [...configs, ...newConfigs];
      const newDefault = fileDefault ?? merged[0]?.name ?? null;

      // Write to .ateliercode/runners.json (primary)
      const runners: AteliercodeRunners = {
        $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
        configs: merged.map((c) => ({ name: c.name, command: c.command, ...(c.shell && c.shell !== 'default' ? { shell: c.shell } : {}) })),
        default: newDefault,
      };
      await backend.files.writeAteliercodeRunners(project.id, JSON.stringify(runners, null, 2));

      // Update local state
      let counter = 0;
      setFileConfigs(merged.map((c) => ({
        ...c,
        id: Date.now().toString(36) + (++counter).toString(36),
      })));
      setFileDefault(newDefault);

      // Also keep settings blob in sync for backward compat
      const updatedSettings = setProjectRunnerSettings(project.settings, {
        configs: merged,
        defaultConfigId: merged[0]?.id ?? null,
      });
      await backend.projects.update(project.id, { settings: updatedSettings });
      await updateProjectInStore(project.id, { settings: updatedSettings });

      setDetectedScripts(null);
    } catch {
      // silent — user can retry
    } finally {
      setIsAutoConfiguring(false);
      setMenuOpen(false);
    }
  }, [project, backend, configs, fileDefault, updateProjectInStore]);

  const defaultConfig = configs.find((c) => c.id === defaultConfigId) ?? configs[0];

  if (compact) {
    return (
      <>
        <button
          onClick={handleMainClick}
          className={`btn btn-ghost btn-xs btn-square ${isRunning ? 'text-error' : 'text-success'}`}
          title={isRunning ? 'Stop' : defaultConfig ? `Run: ${defaultConfig.name}` : 'Run'}
        >
          {isRunning ? (
            <Square className="w-3.5 h-3.5" />
          ) : (
            <Play className="w-3.5 h-3.5" />
          )}
        </button>
        {renderDropdown()}
      </>
    );
  }

  return (
    <div className="flex items-center">
      <button
        onClick={handleMainClick}
        className={`flex items-center gap-1 h-6 px-1.5 rounded-l transition-colors ${
          isRunning
            ? 'text-error hover:bg-error/10'
            : 'text-emerald-400 hover:text-emerald-300 hover:bg-base-300'
        }`}
        title={isRunning ? 'Stop' : defaultConfig ? `Run: ${defaultConfig.name}` : 'Run'}
      >
        {isRunning ? (
          <Square className="w-3.5 h-3.5" />
        ) : (
          <Play className="w-3.5 h-3.5" />
        )}
        <span className="text-xs">{isRunning ? 'Stop' : 'Run'}</span>
      </button>
      <div className="w-px h-4 bg-base-300" />
      <button
        ref={chevronRef}
        onClick={toggleMenu}
        className={`flex items-center justify-center h-6 px-1 rounded-r transition-colors ${
          menuOpen
            ? 'bg-base-300 text-base-content'
            : 'text-base-content/50 hover:text-base-content hover:bg-base-300'
        }`}
        title="Run configurations"
      >
        <ChevronDown className="w-3 h-3" />
      </button>
      {renderDropdown()}
    </div>
  );

  function renderDropdown() {
    if (!menuOpen) return null;
    return createPortal(
      <div
        ref={menuRef}
        className="fixed z-[9999] bg-base-100 border border-base-300 rounded-lg shadow-xl py-1 w-56"
        style={{ top: menuPos.top, left: menuPos.left }}
      >
        {configs.length > 0 ? (
          <>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-base-content/50 font-semibold select-none">
              Run Configurations
            </div>
            {configs.map((config) => (
              <button
                key={config.id}
                onClick={() => runConfig(config)}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content hover:bg-base-200 hover:text-base-content cursor-pointer transition-colors text-left"
              >
                <Play className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                <span className="truncate flex-1">{config.name}</span>
                {config.id === defaultConfigId && (
                  <Star className="w-3 h-3 text-amber-500 shrink-0 fill-amber-500" />
                )}
              </button>
            ))}
            <div className="border-t border-base-300 my-1" />
          </>
        ) : detectedScripts && detectedScripts.length > 0 ? (
          <>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-base-content/50 font-semibold select-none">
              Detected Scripts
            </div>
            {detectedScripts.map((script) => (
              <button
                key={script.id}
                onClick={() => runConfig(script)}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content hover:bg-base-200 hover:text-base-content cursor-pointer transition-colors text-left"
              >
                <Play className="w-3.5 h-3.5 text-emerald-500/60 shrink-0" />
                <span className="truncate">{script.name}</span>
              </button>
            ))}
            <div className="border-t border-base-300 my-1" />
          </>
        ) : (
          <div className="px-3 py-2 text-xs text-base-content/50">
            No run configurations
          </div>
        )}

        {/* Recent History */}
        {history.length > 0 && (
          <>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-base-content/50 font-semibold select-none">
              Recent
            </div>
            {history.slice(0, 5).map((entry, i) => {
              // Skip if already shown in configs
              const alreadyInConfigs = configs.some((c) => c.command === entry.command);
              if (alreadyInConfigs) return null;
              return (
                <button
                  key={`history-${i}`}
                  onClick={() => runConfig({ id: `history-${i}`, name: entry.name, command: entry.command })}
                  className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content hover:bg-base-200 hover:text-base-content cursor-pointer transition-colors text-left"
                >
                  <History className="w-3.5 h-3.5 text-base-content/50 shrink-0" />
                  <span className="truncate flex-1">{entry.name}</span>
                </button>
              );
            })}
            <div className="border-t border-base-300 my-1" />
          </>
        )}

        {/* Auto Configure */}
        <button
          onClick={handleAutoConfigure}
          disabled={isAutoConfiguring}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content/70 hover:bg-base-200 hover:text-base-content cursor-pointer transition-colors text-left disabled:opacity-50"
        >
          {isAutoConfiguring ? (
            <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin" />
          ) : (
            <Wand2 className="w-3.5 h-3.5 shrink-0" />
          )}
          <span>{isAutoConfiguring ? 'Detecting...' : 'Auto Configure'}</span>
        </button>

        {/* Edit Configurations */}
        <button
          onClick={() => { setMenuOpen(false); openSettings(); }}
          className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-base-content/70 hover:bg-base-200 hover:text-base-content cursor-pointer transition-colors text-left"
        >
          <Settings className="w-3.5 h-3.5 shrink-0" />
          <span>Edit Configurations...</span>
        </button>
      </div>,
      document.body,
    );
  }
}
